import { supabase } from '../integrations/supabase/client';

export interface AuditLogParams {
  resourceType: string;
  resourceId: string;
  action: string;
  userId?: string;
  userEmail?: string;
  tenantId?: string;
  metadata?: Record<string, any>;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Comprehensive audit logging utility
 * Captures all administrative actions for compliance and security
 */
export class AuditLogger {
  /**
   * Log an action to the audit trail
   */
  static async log(params: AuditLogParams): Promise<void> {
    try {
      const { error } = await supabase.from('audit_logs').insert({
        resource_type: params.resourceType,
        resource_id: params.resourceId,
        action: params.action,
        user_id: params.userId,
        user_email: params.userEmail,
        tenant_id: params.tenantId,
        metadata: params.metadata || {},
        old_values: params.oldValues,
        new_values: params.newValues,
        ip_address: params.ipAddress,
        user_agent: params.userAgent || navigator.userAgent,
      });

      if (error) {
        console.error('[Audit] Failed to log action:', error);
      }
    } catch (error) {
      console.error('[Audit] Exception logging action:', error);
    }
  }

  /**
   * Log client-related actions
   */
  static async logClientAction(
    action: 'created' | 'updated' | 'suspended' | 'credit_approved' | 'credit_declined',
    clientId: string,
    metadata?: Record<string, any>,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>
  ) {
    const user = (await supabase.auth.getUser()).data.user;
    
    return this.log({
      resourceType: 'client',
      resourceId: clientId,
      action: `client.${action}`,
      userId: user?.id,
      userEmail: user?.email,
      tenantId: clientId,
      metadata,
      oldValues,
      newValues,
    });
  }

  /**
   * Log hierarchy-related actions
   */
  static async logHierarchyAction(
    action: 'created' | 'updated' | 'deleted',
    hierarchyId: string,
    parentClientId: string,
    childClientId: string,
    metadata?: Record<string, any>
  ) {
    const user = (await supabase.auth.getUser()).data.user;
    
    return this.log({
      resourceType: 'client_hierarchy',
      resourceId: hierarchyId,
      action: `hierarchy.${action}`,
      userId: user?.id,
      userEmail: user?.email,
      tenantId: parentClientId,
      metadata: {
        ...metadata,
        parent_client_id: parentClientId,
        child_client_id: childClientId,
      },
    });
  }

  /**
   * Log configuration-related actions
   */
  static async logConfigAction(
    action: 'created' | 'updated' | 'deleted',
    configId: string,
    clientId: string,
    configKey: string,
    metadata?: Record<string, any>,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>
  ) {
    const user = (await supabase.auth.getUser()).data.user;
    
    return this.log({
      resourceType: 'client_configuration',
      resourceId: configId,
      action: `configuration.${action}`,
      userId: user?.id,
      userEmail: user?.email,
      tenantId: clientId,
      metadata: {
        ...metadata,
        config_key: configKey,
      },
      oldValues,
      newValues,
    });
  }

  /**
   * Log user-related actions
   */
  static async logUserAction(
    action: 'invited' | 'activated' | 'deactivated' | 'role_changed',
    userId: string,
    clientId: string,
    metadata?: Record<string, any>
  ) {
    const currentUser = (await supabase.auth.getUser()).data.user;
    
    return this.log({
      resourceType: 'user',
      resourceId: userId,
      action: `user.${action}`,
      userId: currentUser?.id,
      userEmail: currentUser?.email,
      tenantId: clientId,
      metadata,
    });
  }

  /**
   * Log onboarding-related actions
   */
  static async logOnboardingAction(
    action: 'created' | 'completed' | 'cancelled' | 'reminder_sent',
    flowId: string,
    clientId: string,
    metadata?: Record<string, any>
  ) {
    const user = (await supabase.auth.getUser()).data.user;
    
    return this.log({
      resourceType: 'onboarding_flow',
      resourceId: flowId,
      action: `onboarding.${action}`,
      userId: user?.id,
      userEmail: user?.email,
      tenantId: clientId,
      metadata,
    });
  }

  /**
   * Log credit-related actions
   */
  static async logCreditAction(
    action: 'applied' | 'approved' | 'declined' | 'limit_changed',
    applicationId: string,
    clientId: string,
    metadata?: Record<string, any>,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>
  ) {
    const user = (await supabase.auth.getUser()).data.user;
    
    return this.log({
      resourceType: 'credit_application',
      resourceId: applicationId,
      action: `credit.${action}`,
      userId: user?.id,
      userEmail: user?.email,
      tenantId: clientId,
      metadata,
      oldValues,
      newValues,
    });
  }

  /**
   * Log portal access actions (view client portal)
   */
  static async logPortalAccessAction(
    action: 'view_started' | 'view_ended',
    clientId: string,
    metadata?: Record<string, any>
  ) {
    const user = (await supabase.auth.getUser()).data.user;
    
    return this.log({
      resourceType: 'client',
      resourceId: clientId,
      action: `portal_access.${action}`,
      userId: user?.id,
      userEmail: user?.email,
      tenantId: clientId,
      metadata,
    });
  }
}
